import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ProjectDetailsDialogCtrl } from '../../../ts/controllers/ProjectDetailsDialogCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { Filter } from '../../../ts/helpers/Filter';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { ProjectDefinition } from '../../../ts/models/ContentDefinitions';
import { ProjectContentType } from '../../../ts/models/ContentTypes';
import { IProjectDetailInfo, IProjectInfo } from '../../../ts/models/IProjectInfo';
import { Projects } from '../../../ts/resources/Projects';
import { FavoriteItemType } from '../../../ts/ServerApi/types/IFavorite';
import { IProjectActions } from '../../../ts/ServerApi/types/IProjectActions';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { ModalService } from '../../../ts/services/ModalService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../actions/spaces/FavoritesActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs } from '../../content/breadcrumbs/BreadcrumbHelpers';
import { usePrevious } from '../../hooks';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { DetailsDialogInAngularPlaceHelper } from '../DetailsDialog/AngularContentDetailsDialog';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { RequestAccessError } from '../RouterHelpers/RequestAccessError';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedProjectSpace } from './ConnectedProjectSpace';
import { ProjectSpaceOwnProps } from './ProjectSpace';

export interface ProjectSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly routeLocationSearch: string;
  readonly projectId: string;
  readonly urls: SpacesSiteUrls;
  readonly renderProjectSpace: (props: ProjectSpaceOwnProps) => JSX.Element;
  readonly setFetchedAllData: (isFetched: boolean) => void;
}

export interface ProjectSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly addContentFavorite: (contentId: string, contentType: FavoriteItemType) => void;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: FavoriteItemType, contentName: string) => void;
}

export interface ProjectSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
}

export interface ProjectSpaceProviderPropsFromAngular {
  readonly Projects: Projects;
  readonly telemetry: Telemetry;
  readonly sanitizer: ng.sanitize.ISanitizeService;
  readonly browserTitleService: BrowserTitleService;
  readonly modalService: ModalService;
  readonly q: ng.IQService;
}

export type ProjectSpaceNonAngularProps = ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps & ProjectSpaceProviderDispatchProps;

export type ProjectSpaceProviderContainerProps =  ProjectSpaceProviderStateProps & ProjectSpaceProviderOwnProps &  ProjectSpaceProviderDispatchProps & ProjectSpaceProviderPropsFromAngular;

export interface ProjectSpaceProviderContainerState {
  readonly projectInfo: IProjectInfo;
  readonly projectDetail: IProjectDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly projectActions: IProjectActions;
  readonly errorCode: Number;
}

const InitialState: ProjectSpaceProviderContainerState = {
  projectInfo: undefined,
  projectDetail: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  projectActions: undefined,
  errorCode: undefined
};

export class ProjectSpaceProviderContainer extends React.PureComponent<ProjectSpaceProviderContainerProps, ProjectSpaceProviderContainerState> {

  private detailsDialogHelper: DetailsDialogInAngularPlaceHelper;
  private unmounted: boolean = false;

  public constructor(props: ProjectSpaceProviderContainerProps){
    super(props);
    this.state =  InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderProjectSpace({
        projectDetail: {
          usageInfo: this.state.projectInfo.usageInfo,
          usageInfoMaxValues: this.state.projectInfo.usageInfoMaxValues,
          ...this.state.projectDetail
        },
        projectId: this.state.projectDetail.id,
        openDetail: this.openDetail,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        sanitizer: this.props.sanitizer,
        telemetry: this.props.telemetry,
        angularContext: this.props.angularContext
      });

    } else if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.projectId} contentType={ProjectContentType} errorCode={this.state.errorCode}/>;
      }
      return <RouterError errorType={'projects'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: ProjectSpaceProviderContainerProps) {
    if(this.changedProject(prevProps)) {
      this.props.setFetchedAllData(false);
      this.updateState(InitialState /* want the initial state for the new project */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.props.enterSpace(
        {
          item: this.state.projectDetail,
          spaceType: ProjectDefinition.contentType,
          subspaceName: undefined
        }
      );

      this.detailsDialogHelper = new DetailsDialogInAngularPlaceHelper(
        this.getData,
        this.props.q,
        this.props.angularContext,
        this.props.modalService,
        this.props.telemetry
      );

      this.props.requestSpaceContents();

      this.setState({initialRender: false, renderChildren: true});

      this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.projectDetail.name });
    }
  }

  private getData = () => {
    this.getProjectInfo();
    this.getProjectDetail();
    this.getActions();
  }

  private openDetail = () => {
    const { projectActions, projectDetail, projectInfo } = this.state;
    this.detailsDialogHelper.openDialog(
      this.state.projectDetail,
      {
        controller: ProjectDetailsDialogCtrl,
        resolve: {
          projectActions: () => projectActions,
          projectDetailInfo: () => projectDetail,
          detailsDialogHelper: () => this.detailsDialogHelper
        }
      }
    );
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.projectDetail.id, 'project');
  }

  private unfavorite = () => {
    this.props.removeContentFavorite(this.state.projectDetail.id, this.state.projectDetail.id, 'project', this.state.projectDetail.name);
  }

  private getProjectInfo = async () => {
    try {
      const project = await this.props.Projects.all()
        .withFilters([Filter.fieldEq('id', this.props.projectId)])
        .getFirst();
      this.updateState({projectInfo: project});
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as Number});
    }
  }

  private getProjectDetail = async () => {
    try {
      const detail = await this.props.Projects.getById(this.props.projectId);
      this.updateState({projectDetail: detail});
      const projectHierarchy = await this.props.Projects.getProjectHierarchy(detail.id);
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      populateContentBreadcrumbs(projectHierarchyWithType, this.props.urls);
      this.props.setFetchedAllData(true);
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as Number});

    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.Projects.actions(this.props.projectId);
      this.updateState({projectActions: actions});
    } catch (e) {}
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.projectInfo) &&
      !_.isUndefined(this.state.projectDetail);
  }

  private changedProject(prevProps: ProjectSpaceProviderContainerProps) {
    return !_.isUndefined(prevProps.projectId)
      && !_.isUndefined(this.props.projectId)
      && prevProps.projectId !== this.props.projectId;
  }

  private updateState<K extends keyof ProjectSpaceProviderContainerState>(newState: Pick<ProjectSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

export namespace ProjectSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const Projects = ServiceLocator.get<Projects>('Projects');
    const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
    const sanitizer = ServiceLocator.get<ng.sanitize.ISanitizeService>('$sanitize');
    const browserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');
    const modalService = ServiceLocator.get<ModalService>('ModalService');
    const q = ServiceLocator.get<ng.IQService>('$q');
    return {
      Projects,
      telemetry,
      sanitizer,
      browserTitleService,
      modalService,
      q,
    };
  }

  function selectPropsFromAngularServices(angularServices: ProjectSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<ProjectSpaceNonAngularProps, ProjectSpaceProviderPropsFromAngular, ProjectSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const ProjectSpaceProviderAng: React.ComponentType<ProjectSpaceNonAngularProps> = ProjectSpaceProviderAngularProvider.HOC(ProjectSpaceProviderContainer);

const mapStateToProps: MapStateToProps<ProjectSpaceProviderStateProps> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<ProjectSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  requestSpaceContents,
  enterSpace,
  addContentFavorite,
  removeContentFavorite,
}, dispatch);

export const ConnectedProjectSpaceProviderContainer: React.ComponentClass<ProjectSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(ProjectSpaceProviderAng);

const renderProjectSpace = (props: ProjectSpaceOwnProps): JSX.Element => <ConnectedProjectSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}

export type ProjectSpaceProviderProps = LocationPathProp & Omit<ProjectSpaceProviderOwnProps, 'renderProjectSpace' | 'setFetchedAllData'>;

export interface ProjectSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export const ProjectSpaceProvider: React.FunctionComponent<ProjectSpaceProviderProps> = React.memo(props => {

  const [fetchedAllData, setFetchedAllData] = React.useState(false);
  const previousProjectId = usePrevious(props.projectId);

  // TFSID:983648.  Handle case when updating this component with a new projectId
  const resetStore = !_.isUndefined(previousProjectId) && !_.isUndefined(props.projectId)
    && previousProjectId !== props.projectId;

  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateContentTypes: true,
    shouldUpdateFilters: true,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: fetchedAllData,
    resetStore: resetStore
  };

  const ProjectSpaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'project',
    subspaceName: undefined
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={ProjectSpaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedProjectSpaceProviderContainer {...{...props, renderProjectSpace, setFetchedAllData: setFetchedAllData}}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
});
